/**
 * Centralized permission and role definitions for the application.
 * Use these constants for access control throughout the codebase.
 *
 * @example
 * import { PERMISSIONS, ROLES, hasPermission } from '@/config/permissions';
 *
 * // Check permission
 * if (hasPermission(user.permissions, PERMISSIONS.USERS.MANAGE)) { ... }
 *
 * // Check role
 * if (hasRole(user.roles, 'admin')) { ... }
 */

// ============================================================================
// PERMISSION CONSTANTS
// ============================================================================

/**
 * All available permissions in the system.
 * Format: DOMAIN.ACTION
 */
export const PERMISSIONS = {
  // User permissions
  USERS: {
    VIEW: 'users:view',
    CREATE: 'users:create',
    UPDATE: 'users:update',
    DELETE: 'users:delete',
    MANAGE: 'users:manage', // Full CRUD
    MANAGE_ROLES: 'users:manage_roles',
  },

  // Project permissions
  PROJECTS: {
    VIEW: 'projects:view',
    CREATE: 'projects:create',
    UPDATE: 'projects:update',
    DELETE: 'projects:delete',
    MANAGE: 'projects:manage',
    ASSIGN_MEMBERS: 'projects:assign_members',
  },

  // Task permissions
  TASKS: {
    VIEW: 'Task.View',
    CREATE: 'Task.Create',
    UPDATE: 'Task.Update',
    DELETE: 'Task.Delete',
    ASSIGN_USERS: 'Task.AssignUsers',
    ADMIN: 'Task.Admin',

    // Task Assignment permissions
    ASSIGNMENT_VIEW: 'Task.Assignment.View',
    ASSIGNMENT_CREATE: 'Task.Assignment.Create',
    ASSIGNMENT_UPDATE: 'Task.Assignment.Update',
    ASSIGNMENT_DELETE: 'Task.Assignment.Delete',

    // Task Comment permissions
    COMMENT_VIEW: 'Task.Comment.View',
    COMMENT_CREATE: 'Task.Comment.Create',
    COMMENT_UPDATE: 'Task.Comment.Update',
    COMMENT_DELETE: 'Task.Comment.Delete',

    // Task Attachment permissions
    ATTACHMENT_VIEW: 'Task.Attachment.View',
    ATTACHMENT_CREATE: 'Task.Attachment.Create',
    ATTACHMENT_UPDATE: 'Task.Attachment.Update',
    ATTACHMENT_DELETE: 'Task.Attachment.Delete',

    // Task Note permissions
    NOTE_VIEW: 'Task.Note.View',
    NOTE_CREATE: 'Task.Note.Create',
    NOTE_UPDATE: 'Task.Note.Update',
    NOTE_DELETE: 'Task.Note.Delete',

    // Task Log permissions
    LOG: 'Task.Log',

    // Task Notification permissions
    NOTIFICATION_VIEW: 'Task.Notification.View',
    NOTIFICATION_CREATE: 'Task.Notification.Create',
    NOTIFICATION_UPDATE: 'Task.Notification.Update',
    NOTIFICATION_DELETE: 'Task.Notification.Delete',

    // Task Notification Queue permissions
    NOTIFICATION_QUEUE_VIEW: 'Task.NotificationQueue.View',
    NOTIFICATION_QUEUE_CREATE: 'Task.NotificationQueue.Create',
    NOTIFICATION_QUEUE_UPDATE: 'Task.NotificationQueue.Update',
    NOTIFICATION_QUEUE_DELETE: 'Task.NotificationQueue.Delete',

    // Lookup management permissions
    PRIORITY: 'Task.Priority',
    STATUS: 'Task.Status',
    PROGRESS_STAGE: 'Task.ProgressStage',
  },

  // Member permissions
  MEMBERS: {
    VIEW: 'members:view',
    CREATE: 'members:create',
    UPDATE: 'members:update',
    DELETE: 'members:delete',
    MANAGE: 'members:manage',
    VIEW_PROFILE: 'members:view_profile',
  },

  // HR Management permissions (backend format: HR.<Resource>.<Action>)
  HR: {
    // Employee
    EMPLOYEES_READ: 'HR.Employees.Read',
    EMPLOYEES_WRITE: 'HR.Employees.Write',
    EMPLOYEES_DELETE: 'HR.Employees.Delete',

    // Employee files
    FILES_READ: 'HR.EmployeeFiles.Read',
    FILES_WRITE: 'HR.EmployeeFiles.Write',
    FILES_DELETE: 'HR.EmployeeFiles.Delete',

    // Leaves
    LEAVES_READ: 'HR.Leaves.Read',
    LEAVES_WRITE: 'HR.Leaves.Write',
    LEAVES_APPROVE: 'HR.Leave.Approve',
    LEAVES_DELETE: 'HR.Leaves.Delete',

    // Leave documents
    LEAVE_DOCS_READ: 'HR.LeaveDocuments.Read',
    LEAVE_DOCS_WRITE: 'HR.LeaveDocuments.Write',
    LEAVE_DOCS_DELETE: 'HR.LeaveDocuments.Delete',

    // Duties + employee duties
    DUTIES_READ: 'HR.Duties.Read',
    DUTIES_WRITE: 'HR.Duties.Write',
    DUTIES_DELETE: 'HR.Duties.Delete',
    EMPLOYEE_DUTIES_READ: 'HR.EmployeeDuties.Read',
    EMPLOYEE_DUTIES_WRITE: 'HR.EmployeeDuties.Write',
    EMPLOYEE_DUTIES_DELETE: 'HR.EmployeeDuties.Delete',

    // Policies + assignments
    POLICIES_READ: 'HR.Policies.Read',
    POLICIES_WRITE: 'HR.Policies.Write',
    POLICIES_ASSIGN: 'HR.Policies.Assign',
    POLICIES_DELETE: 'HR.Policies.Delete',

    // Lookups
    EMPLOYEE_FILE_TYPES_WRITE: 'HR.EmployeeFileTypes.Write',
    EMPLOYEE_FILE_CATEGORIES_WRITE: 'HR.EmployeeFileCategories.Write',
    LEAVE_TYPES_WRITE: 'HR.LeaveTypes.Write',
    LEAVE_STATUS_WRITE: 'HR.LeaveStatus.Write',

    // Settings
    SETTINGS_READ: 'HR.Settings.Read',
    SETTINGS_WRITE: 'HR.Settings.Write',
    NOTIFICATION_SETTINGS_WRITE: 'HR.NotificationSettings.Write',
    PAYSPACE_SYNC: 'HR.PaySpace.Sync',
  },

  // Procurement permissions
  PROCUREMENTS: {
    VIEW: 'procurements:view',
    CREATE: 'procurements:create',
    UPDATE: 'procurements:update',
    DELETE: 'procurements:delete',
    MANAGE: 'procurements:manage',
    APPROVE: 'procurements:approve',
    REJECT: 'procurements:reject',
  },

  // Meeting permissions
  MEETINGS: {
    VIEW: 'meetings:view',
    CREATE: 'meetings:create',
    UPDATE: 'meetings:update',
    DELETE: 'meetings:delete',
    MANAGE: 'meetings:manage',
    SCHEDULE: 'meetings:schedule',
  },

  // Campus Report permissions
  CAMPUS_REPORT: {
    VIEW: 'campus_report:view',
    CREATE: 'campus_report:create',
    UPDATE: 'campus_report:update',
    MANAGE: 'campus_report:manage',
    EXPORT: 'campus_report:export',
  },

  // Communications permissions (backend format: Communications.<Channel>.<Action>)
  // Seeded by migration 165. Each channel gets View/Manage/Send/Settings; plus
  // cross-cutting Campaigns and Analytics rights.
  COMMUNICATIONS: {
    WHATSAPP: {
      VIEW: 'Communications.WhatsApp.View',
      MANAGE: 'Communications.WhatsApp.Manage',
      SEND: 'Communications.WhatsApp.Send',
      SETTINGS: 'Communications.WhatsApp.Settings',
    },
    EMAIL: {
      VIEW: 'Communications.Email.View',
      MANAGE: 'Communications.Email.Manage',
      SEND: 'Communications.Email.Send',
      SETTINGS: 'Communications.Email.Settings',
    },
    SMS: {
      VIEW: 'Communications.SMS.View',
      MANAGE: 'Communications.SMS.Manage',
      SEND: 'Communications.SMS.Send',
      SETTINGS: 'Communications.SMS.Settings',
    },
    PUSH: {
      VIEW: 'Communications.Push.View',
      MANAGE: 'Communications.Push.Manage',
      SEND: 'Communications.Push.Send',
      SETTINGS: 'Communications.Push.Settings',
    },
    CAMPAIGNS: {
      VIEW: 'Communications.Campaigns.View',
      MANAGE: 'Communications.Campaigns.Manage',
      SEND: 'Communications.Campaigns.Send',
    },
    ANALYTICS: {
      VIEW: 'Communications.Analytics.View',
    },
  },

  // Store permissions (backend format: Store.<Area>.<Action>)
  // Seeded by migration 236 alongside the ShopAdmin role. Covers the non-vendor
  // merch catalogue and the legacy shop orders/payments that consume it —
  // vendor-marketplace administration lives in the vendor portal instead.
  STORE: {
    DASHBOARD: {
      VIEW: 'Store.Dashboard.View',
    },
    ORDER: {
      VIEW: 'Store.Order.View',
      CREATE: 'Store.Order.Create',
      UPDATE: 'Store.Order.Update',
      DELETE: 'Store.Order.Delete',
      STATUS: 'Store.Order.Status',
    },
    PAYMENT: {
      VIEW: 'Store.Payment.View',
      RECORD: 'Store.Payment.Record',
    },
    PRODUCT: {
      VIEW: 'Store.Product.View',
      CREATE: 'Store.Product.Create',
      UPDATE: 'Store.Product.Update',
      DELETE: 'Store.Product.Delete',
    },
    STOCK: {
      VIEW: 'Store.Stock.View',
      ADJUST: 'Store.Stock.Adjust',
    },
    EXPORT: 'Store.Export',
  },

  // System permissions
  SYSTEM: {
    ADMIN: 'system:admin',
    SETTINGS: 'system:settings',
    AUDIT_LOG: 'system:audit_log',
    BACKUP: 'system:backup',
  },
} as const;

// ============================================================================
// ROLE DEFINITIONS
// ============================================================================

/** Available role names */
export type RoleName = 'admin' | 'manager' | 'user' | 'viewer' | 'guest';

/**
 * Backend role strings emitted by the JWT (case-sensitive, PascalCase). These
 * are what `<RoleGuard roles={[...]}>` and menu visibility checks compare against,
 * since the auth claims pass through verbatim from the API.
 *
 * Keep in sync with [CcmsAuthorize(Roles=...)] attributes in the backend.
 */
export const BACKEND_ROLES = {
  ADMIN: 'Admin',
  HR_ADMIN: 'HRAdmin',
  SHOP_ADMIN: 'ShopAdmin',
  STAFF: 'Staff',
  MANAGER: 'Manager',
} as const;

export type BackendRole = (typeof BACKEND_ROLES)[keyof typeof BACKEND_ROLES];

/** Role configuration with permissions */
export interface RoleConfig {
  name: RoleName;
  label: string;
  description: string;
  permissions: string[];
}

/**
 * Role definitions with their associated permissions.
 * Roles are hierarchical: admin > manager > user > viewer > guest
 */
export const ROLES: Record<RoleName, RoleConfig> = {
  admin: {
    name: 'admin',
    label: 'Administrator',
    description: 'Full system access with all permissions',
    permissions: [
      // System
      PERMISSIONS.SYSTEM.ADMIN,
      PERMISSIONS.SYSTEM.SETTINGS,
      PERMISSIONS.SYSTEM.AUDIT_LOG,
      PERMISSIONS.SYSTEM.BACKUP,
      // Users
      PERMISSIONS.USERS.MANAGE,
      PERMISSIONS.USERS.MANAGE_ROLES,
      // All domains - manage
      PERMISSIONS.PROJECTS.MANAGE,
      PERMISSIONS.TASKS.MANAGE,
      PERMISSIONS.MEMBERS.MANAGE,
      PERMISSIONS.PROCUREMENTS.MANAGE,
      PERMISSIONS.PROCUREMENTS.APPROVE,
      PERMISSIONS.MEETINGS.MANAGE,
      PERMISSIONS.CAMPUS_REPORT.MANAGE,
      PERMISSIONS.CAMPUS_REPORT.EXPORT,
    ],
  },

  manager: {
    name: 'manager',
    label: 'Manager',
    description: 'Can manage projects, tasks, and team members',
    permissions: [
      // Users - limited
      PERMISSIONS.USERS.VIEW,
      PERMISSIONS.USERS.CREATE,
      PERMISSIONS.USERS.UPDATE,
      // Projects - full
      PERMISSIONS.PROJECTS.MANAGE,
      PERMISSIONS.PROJECTS.ASSIGN_MEMBERS,
      // Tasks - full
      PERMISSIONS.TASKS.MANAGE,
      PERMISSIONS.TASKS.ASSIGN,
      // Members - full
      PERMISSIONS.MEMBERS.MANAGE,
      // Procurements
      PERMISSIONS.PROCUREMENTS.VIEW,
      PERMISSIONS.PROCUREMENTS.CREATE,
      PERMISSIONS.PROCUREMENTS.UPDATE,
      PERMISSIONS.PROCUREMENTS.APPROVE,
      // Meetings
      PERMISSIONS.MEETINGS.MANAGE,
      // Reports
      PERMISSIONS.CAMPUS_REPORT.VIEW,
      PERMISSIONS.CAMPUS_REPORT.CREATE,
      PERMISSIONS.CAMPUS_REPORT.EXPORT,
    ],
  },

  user: {
    name: 'user',
    label: 'User',
    description: 'Standard user with create and edit permissions',
    permissions: [
      // Users
      PERMISSIONS.USERS.VIEW,
      // Projects
      PERMISSIONS.PROJECTS.VIEW,
      PERMISSIONS.PROJECTS.CREATE,
      PERMISSIONS.PROJECTS.UPDATE,
      // Tasks
      PERMISSIONS.TASKS.VIEW,
      PERMISSIONS.TASKS.CREATE,
      PERMISSIONS.TASKS.UPDATE,
      // Members
      PERMISSIONS.MEMBERS.VIEW,
      PERMISSIONS.MEMBERS.VIEW_PROFILE,
      // Procurements
      PERMISSIONS.PROCUREMENTS.VIEW,
      PERMISSIONS.PROCUREMENTS.CREATE,
      // Meetings
      PERMISSIONS.MEETINGS.VIEW,
      PERMISSIONS.MEETINGS.CREATE,
      PERMISSIONS.MEETINGS.SCHEDULE,
      // Reports
      PERMISSIONS.CAMPUS_REPORT.VIEW,
    ],
  },

  viewer: {
    name: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to most resources',
    permissions: [
      PERMISSIONS.USERS.VIEW,
      PERMISSIONS.PROJECTS.VIEW,
      PERMISSIONS.TASKS.VIEW,
      PERMISSIONS.MEMBERS.VIEW,
      PERMISSIONS.MEMBERS.VIEW_PROFILE,
      PERMISSIONS.PROCUREMENTS.VIEW,
      PERMISSIONS.MEETINGS.VIEW,
      PERMISSIONS.CAMPUS_REPORT.VIEW,
    ],
  },

  guest: {
    name: 'guest',
    label: 'Guest',
    description: 'Limited access for external users',
    permissions: [PERMISSIONS.PROJECTS.VIEW, PERMISSIONS.MEETINGS.VIEW],
  },
} as const;

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

/**
 * Check if a user has a specific permission
 * @param userPermissions - Array of user's permissions
 * @param permission - Permission to check
 * @returns True if user has the permission
 */
export function hasPermission(userPermissions: string[], permission: string): boolean {
  // Admin has all permissions
  if (userPermissions.includes(PERMISSIONS.SYSTEM.ADMIN)) {
    return true;
  }
  return userPermissions.includes(permission);
}

/**
 * Check if a user has any of the specified permissions
 * @param userPermissions - Array of user's permissions
 * @param permissions - Permissions to check
 * @returns True if user has any of the permissions
 */
export function hasAnyPermission(userPermissions: string[], permissions: string[]): boolean {
  if (userPermissions.includes(PERMISSIONS.SYSTEM.ADMIN)) {
    return true;
  }
  return permissions.some((permission) => userPermissions.includes(permission));
}

/**
 * Check if a user has all of the specified permissions
 * @param userPermissions - Array of user's permissions
 * @param permissions - Permissions to check
 * @returns True if user has all of the permissions
 */
export function hasAllPermissions(userPermissions: string[], permissions: string[]): boolean {
  if (userPermissions.includes(PERMISSIONS.SYSTEM.ADMIN)) {
    return true;
  }
  return permissions.every((permission) => userPermissions.includes(permission));
}

/**
 * Check if a user has a specific role
 * @param userRoles - Array of user's roles
 * @param role - Role to check
 * @returns True if user has the role
 */
export function hasRole(userRoles: string[], role: RoleName): boolean {
  return userRoles.includes(role);
}

/**
 * Check if a user has any of the specified roles
 * @param userRoles - Array of user's roles
 * @param roles - Roles to check
 * @returns True if user has any of the roles
 */
export function hasAnyRole(userRoles: string[], roles: RoleName[]): boolean {
  return roles.some((role) => userRoles.includes(role));
}

/**
 * Get all permissions for a role
 * @param role - Role name
 * @returns Array of permissions for the role
 */
export function getPermissionsForRole(role: RoleName): string[] {
  return ROLES[role]?.permissions ?? [];
}

/**
 * Get all permissions for multiple roles (combined, deduplicated)
 * @param roles - Array of role names
 * @returns Array of unique permissions
 */
export function getPermissionsForRoles(roles: RoleName[]): string[] {
  const allPermissions = roles.flatMap((role) => getPermissionsForRole(role));
  return [...new Set(allPermissions)];
}

/**
 * Check if a permission string matches a domain
 * @param permission - Permission string
 * @param domain - Domain to match (e.g., 'projects', 'users')
 * @returns True if permission is for the domain
 */
export function isPermissionForDomain(permission: string, domain: string): boolean {
  return permission.startsWith(`${domain}:`);
}

// ============================================================================
// ACCESS RULE TYPE
// ============================================================================

/**
 * Access rule for component/route protection
 */
export interface AccessRule {
  /** Required roles (OR logic by default) */
  roles?: RoleName[];
  /** Required permissions (OR logic by default) */
  permissions?: string[];
  /** If true, requires ALL roles/permissions instead of ANY */
  requireAll?: boolean;
}

/**
 * Evaluate an access rule against user's roles and permissions
 * @param rule - Access rule to evaluate
 * @param userRoles - User's roles
 * @param userPermissions - User's permissions
 * @returns True if access is granted
 */
export function evaluateAccessRule(
  rule: AccessRule | undefined,
  userRoles: string[],
  userPermissions: string[]
): boolean {
  // No rule means access granted
  if (!rule) return true;

  const { roles, permissions, requireAll = false } = rule;

  // Check roles
  const roleCheck =
    !roles || roles.length === 0 || (requireAll ? roles.every((r) => userRoles.includes(r)) : roles.some((r) => userRoles.includes(r)));

  // Check permissions
  const permissionCheck =
    !permissions ||
    permissions.length === 0 ||
    (requireAll ? hasAllPermissions(userPermissions, permissions) : hasAnyPermission(userPermissions, permissions));

  // Both must pass
  return roleCheck && permissionCheck;
}

// Type exports
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS][keyof (typeof PERMISSIONS)[keyof typeof PERMISSIONS]];
